import { GitGraphProtocolError } from '@web-git-graph/protocol';

// src/providers/github.ts
function parseRepository(value) {
  const normalized = value.trim().replace(/^https?:\/\/github\.com\//i, "").replace(/\.git$/, "").replace(/^\/+|\/+$/g, "");
  const [owner, repo, ...rest] = normalized.split("/");
  if (!owner || !repo || rest.length) throw new TypeError(`Invalid GitHub repository: ${value}`);
  return { owner, repo };
}
function toCommit(commit) {
  return {
    oid: commit.sha,
    parents: commit.parents.map((parent) => parent.sha),
    message: commit.commit.message,
    kind: "commit",
    author: {
      name: commit.commit.author?.name ?? commit.author?.login ?? "Unknown",
      ...commit.commit.author?.email ? { email: commit.commit.author.email } : {},
      ...commit.author?.avatar_url ? { avatarUrl: commit.author.avatar_url } : {}
    },
    ...commit.commit.author?.date ? { authoredAt: commit.commit.author.date } : {},
    ...commit.commit.committer?.date ? { committedAt: commit.commit.committer.date } : {},
    url: commit.html_url,
    ...commit.files ? {
      additions: commit.files.reduce((sum, file) => sum + (file.additions ?? 0), 0),
      deletions: commit.files.reduce((sum, file) => sum + (file.deletions ?? 0), 0),
      changedFiles: commit.files.length
    } : {}
  };
}
function changeKind(status) {
  if (status === "added") return "add";
  if (status === "removed") return "delete";
  if (status === "modified" || status === "changed") return "modify";
  if (status === "renamed") return "rename";
  if (status === "copied") return "copy";
  return "unknown";
}
function toChange(file) {
  return {
    path: file.filename,
    kind: changeKind(file.status),
    ...file.previous_filename ? { previousPath: file.previous_filename } : {},
    ...file.additions !== void 0 ? { additions: file.additions } : {},
    ...file.deletions !== void 0 ? { deletions: file.deletions } : {},
    ...!file.patch ? { unavailableReason: "GitHub did not return a textual patch for this file." } : {}
  };
}
function cursor(page, ref) {
  return `${page}:${encodeURIComponent(ref)}`;
}
function parseCursor(value, fallbackRef) {
  if (!value) return { page: 1, ref: fallbackRef };
  const separator = value.indexOf(":");
  const page = Number(value.slice(0, separator));
  const ref = decodeURIComponent(value.slice(separator + 1));
  if (!Number.isInteger(page) || page < 1 || !ref) {
    throw new GitGraphProtocolError("bad_request", "Invalid GitHub pagination cursor.");
  }
  return { page, ref };
}
function revisionOid(revision) {
  if (revision.kind === "working-tree") {
    throw new GitGraphProtocolError("unsupported", "GitHub has no working tree revision.");
  }
  return revision.oid;
}
var GitHubGitGraphProvider = class {
  owner;
  repo;
  #fetch;
  #token;
  #api;
  #pageSize;
  #repository;
  #refs;
  #comparisonCache = /* @__PURE__ */ new Map();
  constructor(options) {
    const resolved = typeof options === "string" ? { repository: options } : options;
    ({ owner: this.owner, repo: this.repo } = parseRepository(resolved.repository));
    this.#fetch = resolved.fetch ?? globalThis.fetch.bind(globalThis);
    this.#token = resolved.token;
    this.#api = (resolved.apiBaseUrl ?? "https://api.github.com").replace(/\/+$/, "");
    this.#pageSize = Math.min(100, Math.max(1, resolved.pageSize ?? 80));
  }
  async getCapabilities() {
    return {
      protocolVersion: "1",
      history: true,
      details: true,
      compare: true,
      diff: true,
      workingTree: false,
      stashes: false,
      maxPageSize: 100,
      maxDiffBytes: 1e6
    };
  }
  async getHistory(request = {}) {
    const repository = await this.#getRepository(request.signal);
    const parsed = parseCursor(
      request.cursor,
      request.refs?.[0] ?? request.ref ?? repository.default_branch
    );
    const limit = Math.min(100, Math.max(1, request.limit ?? this.#pageSize));
    const commits = await this.#request(
      `/repos/${this.owner}/${this.repo}/commits?sha=${encodeURIComponent(parsed.ref)}&per_page=${limit}&page=${parsed.page}`,
      request.signal
    );
    const refs = await this.#getRefs(request.signal);
    return {
      commits: commits.map(toCommit),
      refs,
      head: refs.find((ref) => ref.kind === "current")?.target,
      cursor: commits.length === limit ? cursor(parsed.page + 1, parsed.ref) : void 0,
      hasMore: commits.length === limit,
      repositoryId: repository.full_name,
      repositoryName: repository.full_name
    };
  }
  async getCommitDetails(_repositoryId, revision, signal) {
    const oid = revisionOid(revision);
    const commit = await this.#request(
      `/repos/${this.owner}/${this.repo}/commits/${encodeURIComponent(oid)}`,
      signal
    );
    return {
      commit: toCommit(commit),
      refs: (await this.#getRefs(signal)).filter((ref) => ref.target === oid),
      changes: (commit.files ?? []).map(toChange),
      body: commit.commit.message
    };
  }
  async compare(_repositoryId, base, head, signal) {
    const baseOid = revisionOid(base);
    const headOid = revisionOid(head);
    const result = await this.#getComparison(baseOid, headOid, signal);
    const changes = (result.files ?? []).map(toChange);
    return {
      base,
      head,
      changes,
      additions: changes.reduce((sum, file) => sum + (file.additions ?? 0), 0),
      deletions: changes.reduce((sum, file) => sum + (file.deletions ?? 0), 0),
      truncated: (result.files?.length ?? 0) >= 300
    };
  }
  async getFileDiff(_repositoryId, base, head, path, _context = 3, signal) {
    const baseOid = revisionOid(base);
    const headOid = revisionOid(head);
    const result = await this.#getComparison(baseOid, headOid, signal);
    const file = result.files?.find((item) => item.filename === path);
    if (!file) {
      throw new GitGraphProtocolError(
        "revision_not_found",
        `File ${path} is not part of this comparison.`
      );
    }
    return {
      base,
      head,
      path,
      ...file.previous_filename ? { previousPath: file.previous_filename } : {},
      ...file.patch ? { patch: file.patch } : { unavailableReason: "GitHub omitted this patch because it is binary or too large." }
    };
  }
  async #getRepository(signal) {
    this.#repository ??= await this.#request(
      `/repos/${this.owner}/${this.repo}`,
      signal
    );
    return this.#repository;
  }
  async #getRefs(signal) {
    if (this.#refs) return this.#refs;
    const [repository, heads, tags] = await Promise.all([
      this.#getRepository(signal),
      this.#request(`/repos/${this.owner}/${this.repo}/git/matching-refs/heads/`, signal),
      this.#request(`/repos/${this.owner}/${this.repo}/git/matching-refs/tags/`, signal)
    ]);
    const refs = [
      ...heads.map((ref) => ({
        name: ref.ref,
        target: ref.object.sha,
        kind: "head",
        url: `https://github.com/${this.owner}/${this.repo}/tree/${encodeURIComponent(ref.ref.replace("refs/heads/", ""))}`
      })),
      ...tags.map((ref) => ({
        name: ref.ref,
        target: ref.object.sha,
        kind: "tag",
        url: `https://github.com/${this.owner}/${this.repo}/releases/tag/${encodeURIComponent(ref.ref.replace("refs/tags/", ""))}`
      }))
    ];
    const defaultHead = refs.find((ref) => ref.name === `refs/heads/${repository.default_branch}`);
    if (defaultHead) refs.unshift({ ...defaultHead, name: repository.default_branch, kind: "current" });
    this.#refs = refs;
    return refs;
  }
  async #getComparison(base, head, signal) {
    const key = `${base}...${head}`;
    const cached = this.#comparisonCache.get(key);
    if (cached) return cached;
    const result = await this.#request(
      `/repos/${this.owner}/${this.repo}/compare/${encodeURIComponent(base)}...${encodeURIComponent(head)}`,
      signal
    );
    this.#comparisonCache.set(key, result);
    return result;
  }
  async #request(path, signal) {
    const token = typeof this.#token === "function" ? await this.#token() : this.#token;
    const response = await this.#fetch(`${this.#api}${path}`, {
      signal,
      headers: {
        accept: "application/vnd.github+json",
        "x-github-api-version": "2026-03-10",
        ...token ? { authorization: `Bearer ${token}` } : {}
      }
    });
    if (!response.ok) {
      const reset = response.headers.get("x-ratelimit-reset");
      const body = await response.json().catch(() => ({}));
      const rateLimited = response.status === 403 && response.headers.get("x-ratelimit-remaining") === "0";
      throw new GitGraphProtocolError(
        rateLimited ? "rate_limited" : response.status === 404 ? "repository_not_found" : "internal_error",
        body.message ?? response.statusText,
        {
          retryable: rateLimited || response.status >= 500,
          details: {
            httpStatus: response.status,
            ...reset ? { resetAt: new Date(Number(reset) * 1e3).toISOString() } : {}
          }
        }
      );
    }
    return await response.json();
  }
};

export { GitHubGitGraphProvider };
//# sourceMappingURL=github.js.map
//# sourceMappingURL=github.js.map